import { Suspense, useEffect } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import clsx from 'clsx';
import { useShallow } from 'zustand/react/shallow';
import Icon from '../components/Icon.jsx';
import SyncIndicator from '../components/SyncIndicator.jsx';
import DeckTree from '../features/decks/DeckTree.jsx';
import InstallBanner from '../components/InstallBanner.jsx';
import UpdatePrompt from '../components/UpdatePrompt.jsx';
import { useDeckOverview } from '../hooks/useDecks.js';
import { useGlobalHotkeys } from '../hooks/useHotkeys.js';
import { useSettings } from './settingsStore.js';
import { useAuth } from '../features/auth/authStore.js';
import styles from './AppShell.module.scss';

const NAV = [
  { to: '/', label: 'Decks', icon: 'decks', end: true },
  { to: '/browse', label: 'Browse', icon: 'browse' },
  { to: '/add', label: 'Add', icon: 'plus', primary: true },
  { to: '/study', label: 'Study', icon: 'play', end: true },
  { to: '/settings', label: 'Settings', icon: 'settings' },
];

const SIDE_NAV = [...NAV.filter((n) => !n.primary), { to: '/guide', label: 'Guide', icon: 'help' }];

export default function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const recovery = useAuth((s) => s.recovery);
  const overview = useDeckOverview();
  const { mode, sideDecks, tabLabels, onboarded, set } = useSettings(
    useShallow((s) => ({
      mode: s.sidebar,
      sideDecks: s.sidebarDecks,
      tabLabels: s.tabLabels,
      onboarded: s.onboarded,
      set: s.set,
    })),
  );
  const studyQuery = new URLSearchParams(location.search);
  const focusMode =
    location.pathname.startsWith('/study/') ||
    (location.pathname === '/study' && (studyQuery.has('custom') || studyQuery.get('go') === 'today'));
  const slim = mode === 'icons';
  useGlobalHotkeys(!focusMode);

  useEffect(() => {
    document.documentElement.classList.toggle('study-mode', focusMode);
  }, [focusMode]);

  useEffect(() => {
    if (!recovery) return;
    const updating = location.pathname === '/auth' && new URLSearchParams(location.search).get('mode') === 'update';
    if (!updating) navigate('/auth?mode=update', { replace: true });
  }, [recovery, location.pathname, location.search, navigate]);

  // First launch: a brand-new, empty app starts with the welcome tour.
  const hasDecks = overview ? overview.tree.length > 0 : null;
  useEffect(() => {
    if (onboarded || hasDecks === null) return;
    if (hasDecks) set({ onboarded: true });
    else if (location.pathname === '/' && !location.search) navigate('/welcome', { replace: true });
  }, [onboarded, hasDecks, location.pathname, location.search, navigate, set]);

  const toggleSide = () => set({ sidebar: slim ? 'full' : 'icons' });

  return (
    <div
      className={clsx(
        styles.shell,
        styles[`nav_${mode}`],
        !tabLabels && styles.noLabels,
        focusMode && styles.focus,
      )}
    >
      <aside className={styles.sidebar} aria-label="Side menu">
        <div className={styles.sideTop}>
          <Link to="/" className={styles.brand} title="Recall">
            <img src="/favicon.svg" alt="" width="32" height="32" />
            <span className={styles.sideText}>Recall</span>
          </Link>
          <button
            type="button"
            className={styles.collapse}
            onClick={toggleSide}
            aria-label={slim ? 'Expand side menu' : 'Collapse side menu'}
            title={`${slim ? 'Expand' : 'Collapse'} side menu ( [ )`}
          >
            <Icon name={slim ? 'sidebarOpen' : 'sidebarClose'} size={20} />
          </button>
        </div>
        <Link to="/add" className={styles.sideAdd} title="Add a card" aria-label="Add a card">
          <Icon name="plus" size={20} stroke={2.5} />
          <span className={styles.sideText}>Add a card</span>
        </Link>
        <nav className={styles.sideNav} aria-label="Main">
          {SIDE_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              title={slim ? item.label : undefined}
              aria-label={slim ? item.label : undefined}
              className={({ isActive }) => clsx(styles.sideLink, isActive && styles.active)}
            >
              <Icon name={item.icon} size={20} />
              <span className={styles.sideText}>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        {!slim && sideDecks && overview?.tree.length ? (
          <div className={styles.sideDecks}>
            <p className={styles.sideHeading}>Your decks</p>
            <DeckTree tree={overview.tree} counts={overview.counts} compact />
          </div>
        ) : (
          <div className={styles.sideSpacer} />
        )}
        <div className={styles.sideFooter}>
          <SyncIndicator compact={slim} />
        </div>
      </aside>

      <main className={styles.main} id="main">
        <InstallBanner />
        <Suspense fallback={<div className={styles.loading} aria-busy="true" />}>
          <div key={location.pathname} className={styles.stage}>
            <Outlet />
          </div>
        </Suspense>
      </main>

      <nav className={styles.bottomNav} aria-label="Primary">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            aria-label={tabLabels ? undefined : item.label}
            className={({ isActive }) =>
              clsx(styles.tab, item.primary && styles.tabPrimary, isActive && styles.active)
            }
          >
            <span className={styles.tabIcon}>
              <Icon name={item.icon} size={22} stroke={item.primary ? 2.6 : 2} />
            </span>
            {tabLabels ? <span className={styles.tabLabel}>{item.label}</span> : null}
          </NavLink>
        ))}
      </nav>
      <UpdatePrompt />
    </div>
  );
}
